/**
 * TrafficSystem: keeps a population of NPC cars (with seated driver peds) around the player,
 * drives them along the road graph with obstacle avoidance, and fills ~40% of the parking
 * spots near the player with static cars.
 * PedSystem: spawns / despawns civilian peds on the walk graph and relays scary events.
 */
import * as THREE from 'three';
import { G } from './ctx';
import { Ped } from './ped';
import { clamp, angleDiff, makeRng } from './util';
import { chooseNext, laneHeading, laneIndex, projDist2, projectT, randomLane } from './npc_lanes';
import type { IVehicle, PathLane, VehicleType } from './types';

const _f = new THREE.Vector3();

/** `?npcScale=0.4` scales every population target (headless tests / weak machines). 1 = spec values. */
const NPC_SCALE = (() => {
  try {
    const v = Number(new URLSearchParams(location.search).get('npcScale'));
    return v > 0 && v <= 3 ? v : 1;
  } catch {
    return 1;
  }
})();

/** true when a ground point is plausibly on screen (inside the camera's forward cone and within maxDist) */
function inView(x: number, z: number, maxDist: number): boolean {
  const cam = G.camera;
  const dx = x - cam.position.x, dz = z - cam.position.z;
  const d = Math.hypot(dx, dz);
  if (d > maxDist) return false;
  if (d < 1e-3) return true;
  _f.set(0, 0, -1).applyQuaternion(cam.quaternion);
  const fl = Math.hypot(_f.x, _f.z) || 1;
  const dot = (dx * _f.x + dz * _f.z) / (d * fl);
  return dot >= 0.2;
}

function weightedType(rng: () => number, table: readonly (readonly [VehicleType, number])[]): VehicleType {
  let total = 0;
  for (let i = 0; i < table.length; i++) total += table[i][1];
  let r = rng() * total;
  for (let i = 0; i < table.length; i++) {
    r -= table[i][1];
    if (r <= 0) return table[i][0];
  }
  return table[table.length - 1][0];
}

// ================================================================== traffic ====

const TRAFFIC_TYPES: readonly (readonly [VehicleType, number])[] = [
  ['sedan', 30],
  ['hatch', 25],
  ['taxi', 12],
  ['suv', 12],
  ['van', 8],
  ['sports', 6],
  ['truck', 4],
  ['bus', 3],
];
const PARKED_TYPES: readonly (readonly [VehicleType, number])[] = [
  ['sedan', 34],
  ['hatch', 30],
  ['suv', 14],
  ['van', 10],
  ['taxi', 7],
  ['sports', 5],
];
/** parked cars kept alive at once (40% of the nearest spots up to this budget: draw-call budget on laptop GPUs) */
const PARKED_CAP = 60;
const PARKED_CAP_MOBILE = 28;
const CRUISE: Record<VehicleType, number> = { sedan: 13, hatch: 12.5, taxi: 14, suv: 12.5, van: 11.5, sports: 15, truck: 9, bus: 9, police: 14 };

interface AiCar {
  v: IVehicle;
  ped: Ped;
  laneIdx: number;
  nextLaneIdx: number;
  cruise: number;
  blocked: number;
  stopUntil: number;
  hornUntil: number;
  nextHonk: number;
  aroundUntil: number;
  aroundSign: number;
  /** seconds spent (nearly) stationary while out of the player's range: recycled when too long */
  idleFar: number;
}

export class TrafficSystem {
  private cars: AiCar[] = [];
  private byVehicle = new Map<IVehicle, AiCar>();
  private parkedBySpot = new Map<number, IVehicle>();
  private popTimer = 0;
  private parkTimer = 0;
  private burst = true;
  private readonly parkSeed: number;

  constructor() {
    this.parkSeed = Math.floor(G.rng() * 1e9);
    G.events.on('vehicleCrash', (e) => {
      const c = this.byVehicle.get(e.vehicle);
      if (c && e.speed > 12) c.stopUntil = G.time.t + 4;
    });
  }

  /** AI-driven cars currently managed (read-only, for debugging / other systems) */
  get aiVehicles(): IVehicle[] {
    return this.cars.map((c) => c.v);
  }

  update(dt: number): void {
    const t = G.time.t;
    this.drive(dt, t);
    this.popTimer -= dt;
    if (this.popTimer <= 0) {
      this.popTimer = this.burst ? 0.05 : 0.3;
      this.population(t);
    }
    this.parkTimer -= dt;
    if (this.parkTimer <= 0) {
      this.parkTimer = 0.5;
      this.parking();
    }
    if (this.burst && t > 1.5) this.burst = false;
  }

  // ---- population --------------------------------------------------------------

  private population(t: number): void {
    const px = G.player.pos.x, pz = G.player.pos.z;
    // despawn / drop
    for (let i = this.cars.length - 1; i >= 0; i--) {
      const c = this.cars[i];
      const v = c.v;
      if (v.destroyed || v.driver !== c.ped) {
        // stolen, driver dead or car wrecked: no longer ours
        this.dropAt(i);
        const p = c.ped;
        if (!v.destroyed && v.driver !== c.ped && p.state !== 'dead' && !p.vehicle && p.state !== 'flee') p.scare(v.pos.x, v.pos.z);
        continue;
      }
      const dx = v.pos.x - px, dz = v.pos.z - pz;
      const d2 = dx * dx + dz * dz;
      const far = d2 > 320 * 320;
      const stale = c.idleFar > 25 && d2 > 110 * 110 && !inView(v.pos.x, v.pos.z, 400);
      if ((far || stale) && !v.persistent && t - v.lastPlayerContact > 30) {
        this.dropAt(i);
        G.entities.removeVehicle(v);
        G.entities.removePed(c.ped);
      }
    }
    const target = Math.round((G.isMobile ? 13 : 26) * NPC_SCALE);
    if (this.cars.length < target) {
      const n = this.burst ? 3 : 1;
      for (let k = 0; k < n && this.cars.length < target; k++) this.trySpawn(px, pz);
    }
  }

  private dropAt(i: number): void {
    const c = this.cars[i];
    this.byVehicle.delete(c.v);
    this.cars.splice(i, 1);
    c.v.controls.throttle = 0;
    c.v.controls.steer = 0;
    c.v.controls.horn = false;
    c.v.controls.handbrake = false;
  }

  private trySpawn(px: number, pz: number): void {
    const g = G.city.roadGraph;
    const lanes = g.lanes;
    if (lanes.length === 0) return;
    const rng = G.rng;
    const minD = this.burst ? 40 : 90;
    for (let attempt = 0; attempt < 8; attempt++) {
      const li = randomLane(g, rng);
      if (li < 0) return;
      const lane = lanes[li];
      if (lane.length < 6) continue;
      const k = 0.1 + rng() * 0.7;
      const x = lane.a.x + (lane.b.x - lane.a.x) * k;
      const z = lane.a.z + (lane.b.z - lane.a.z) * k;
      const dx = x - px, dz = z - pz;
      const d = Math.hypot(dx, dz);
      if (d < minD || d > 230) continue;
      if (!this.burst && inView(x, z, 150)) continue;
      if (G.city.isWater(x, z)) continue;
      if (G.entities.nearestVehicle(x, z, 12)) continue;
      const heading = laneHeading(lane);
      const type = weightedType(rng, TRAFFIC_TYPES);
      const v = G.entities.spawnVehicle(type, x, z, heading);
      const ped = G.entities.spawnPed('civilian', x, z, { controlled: true }) as Ped;
      ped.releaseOnExit = true;
      ped.enterVehicle(v, 0);
      const cruise = CRUISE[type] + (rng() - 0.5) * 2;
      // already rolling so a freshly spawned car never looks parked in the lane
      const sp = cruise * 0.6;
      v.speed = sp;
      v.velocity.set(Math.sin(heading) * sp, 0, Math.cos(heading) * sp);
      const c: AiCar = {
        v,
        ped,
        laneIdx: li,
        nextLaneIdx: chooseNext(g, li, rng, 0.6),
        cruise,
        blocked: 0,
        stopUntil: 0,
        hornUntil: 0,
        nextHonk: 0,
        aroundUntil: 0,
        aroundSign: 1,
        idleFar: 0,
      };
      this.cars.push(c);
      this.byVehicle.set(v, c);
      return;
    }
  }

  // ---- parked cars ----------------------------------------------------------------

  private spotOccupied(i: number): boolean {
    return makeRng(this.parkSeed + i * 7919)() < 0.4;
  }

  private spotOrder: Int32Array | null = null;
  private spotD2: Float32Array | null = null;

  private parking(): void {
    const spots = G.city.parkingSpots;
    if (!spots || spots.length === 0) return;
    const px = G.player.pos.x, pz = G.player.pos.z;
    const n = spots.length;
    if (!this.spotOrder || this.spotOrder.length !== n) {
      this.spotOrder = new Int32Array(n);
      for (let i = 0; i < n; i++) this.spotOrder[i] = i;
      this.spotD2 = new Float32Array(n);
    }
    const order = this.spotOrder, d2s = this.spotD2!;
    for (let i = 0; i < n; i++) {
      const s = spots[i];
      const dx = s.pos.x - px, dz = s.pos.z - pz;
      d2s[i] = dx * dx + dz * dz;
    }
    // nearest spots first, so the budget always goes to what the player can see
    order.sort((a, b) => d2s[a] - d2s[b]);
    // release far spots first (frees budget)
    for (let k = n - 1; k >= 0; k--) {
      const i = order[k];
      if (d2s[i] <= 240 * 240) break;
      const v = this.parkedBySpot.get(i);
      if (!v) continue;
      this.parkedBySpot.delete(i);
      if (!v.persistent && !v.playerTouched && !v.destroyed && v.driver === null && G.entities.vehicles.indexOf(v) >= 0) G.entities.removeVehicle(v);
    }
    const cap = Math.round((G.isMobile ? PARKED_CAP_MOBILE : PARKED_CAP) * NPC_SCALE);
    let spawned = 0;
    let count = this.parkedBySpot.size;
    for (let k = 0; k < n; k++) {
      const i = order[k];
      if (d2s[i] >= 160 * 160) break;
      if (count >= cap || spawned >= (this.burst ? 40 : 2)) break;
      if (this.parkedBySpot.has(i)) continue;
      if (!this.spotOccupied(i)) continue;
      const s = spots[i];
      if (G.entities.nearestVehicle(s.pos.x, s.pos.z, 4)) continue;
      if (!this.burst && inView(s.pos.x, s.pos.z, 90)) continue;
      const r = makeRng(this.parkSeed + i * 104729 + 1);
      const type = weightedType(r, PARKED_TYPES);
      const v = G.entities.spawnVehicle(type, s.pos.x, s.pos.z, s.heading, { parked: true });
      this.parkedBySpot.set(i, v);
      spawned++;
      count++;
    }
  }

  // ---- driving AI ---------------------------------------------------------------------

  private drive(dt: number, t: number): void {
    const g = G.city.roadGraph;
    const lanes = g.lanes;
    const px = G.player.pos.x, pz = G.player.pos.z;
    for (let i = 0; i < this.cars.length; i++) {
      const c = this.cars[i];
      const v = c.v;
      const ctl = v.controls;
      if (v.destroyed || v.driver !== c.ped) continue; // dropped on the next population pass
      const ped = c.ped;
      if (ped.state === 'dead') {
        // driver died at the wheel: the car brakes until the body is out (ped exits on its own update)
        ctl.throttle = Math.abs(v.speed) > 0.5 ? -1 : 0;
        ctl.steer = 0;
        ctl.handbrake = Math.abs(v.speed) <= 0.5;
        ctl.horn = false;
        continue;
      }
      if (ped.wantsOut) {
        ctl.throttle = Math.abs(v.speed) > 0.5 ? -1 : 0;
        ctl.steer = 0;
        ctl.handbrake = true;
        ctl.horn = false;
        if (Math.abs(v.speed) < 0.8) {
          ped.exitVehicle();
          ped.scare(v.pos.x, v.pos.z);
        }
        continue;
      }
      if (lanes.length === 0) {
        ctl.throttle = 0;
        ctl.steer = 0;
        continue;
      }
      // ---- lane bookkeeping
      if (c.laneIdx < 0 || c.laneIdx >= lanes.length) {
        const n = g.nearest(v.pos.x, v.pos.z);
        const idx = laneIndex(g, n.id);
        if (idx < 0) {
          ctl.throttle = 0;
          continue;
        }
        c.laneIdx = idx;
        c.nextLaneIdx = chooseNext(g, idx, G.rng, 0.6);
      }
      let lane: PathLane = lanes[c.laneIdx];
      let tp = projectT(lane, v.pos.x, v.pos.z);
      if (projDist2 > 100) {
        // shoved off the road (crash): re-acquire the closest lane
        c.laneIdx = -1;
        ctl.throttle = 0;
        ctl.steer = 0;
        continue;
      }
      const len = lane.length > 0.01 ? lane.length : 0.01;
      if (tp * len >= len - 0.6) {
        if (c.nextLaneIdx >= 0) {
          c.laneIdx = c.nextLaneIdx;
          c.nextLaneIdx = chooseNext(g, c.laneIdx, G.rng, 0.6);
          lane = lanes[c.laneIdx];
          tp = projectT(lane, v.pos.x, v.pos.z);
        } else {
          // dead end (street ending at the river bank): U-turn onto the opposite lane, 7 m to our left
          const lx = Math.cos(v.heading), lz = -Math.sin(v.heading);
          const n = g.nearest(v.pos.x + lx * 7, v.pos.z + lz * 7);
          const idx = laneIndex(g, n.id);
          if (idx >= 0 && idx !== c.laneIdx && Math.abs(angleDiff(v.heading, laneHeading(lanes[idx]))) > 2.0) {
            c.laneIdx = idx;
            c.nextLaneIdx = chooseNext(g, idx, G.rng, 0.6);
            lane = lanes[idx];
            tp = projectT(lane, v.pos.x, v.pos.z);
          } else {
            c.laneIdx = -1;
            c.idleFar += dt * 4; // nowhere to go: let the recycler take it when out of sight
            ctl.throttle = 0;
            ctl.steer = 0;
            ctl.handbrake = true;
            continue;
          }
        }
      }
      const curLen = lane.length > 0.01 ? lane.length : 0.01;
      const spd = v.speed;
      const look = clamp(6 + Math.abs(spd) * 0.45, 6, 12);
      const s = tp * curLen + look;
      let tx: number, tz: number;
      if (s <= curLen) {
        const k = s / curLen;
        tx = lane.a.x + (lane.b.x - lane.a.x) * k;
        tz = lane.a.z + (lane.b.z - lane.a.z) * k;
      } else if (c.nextLaneIdx >= 0) {
        const nl = lanes[c.nextLaneIdx];
        const nlen = nl.length > 0.01 ? nl.length : 0.01;
        const k = clamp((s - curLen) / nlen, 0, 1);
        tx = nl.a.x + (nl.b.x - nl.a.x) * k;
        tz = nl.a.z + (nl.b.z - nl.a.z) * k;
      } else {
        tx = lane.b.x;
        tz = lane.b.z;
      }
      const angleTo = Math.atan2(tx - v.pos.x, tz - v.pos.z);
      const diff = angleDiff(v.heading, angleTo);
      let steer = clamp(-diff * 2, -1, 1);

      // ---- target speed
      let target = c.cruise;
      const remaining = (1 - tp) * curLen;
      if (lane.endsAtIntersection && remaining < 12) target = Math.min(target, 5);
      const ad = Math.abs(diff);
      if (ad > 0.35) target = Math.min(target, ad > 0.9 ? 4 : 7);
      if (t < c.stopUntil) target = 0;

      // ---- obstacles
      const horizon = c.blocked > 4 ? 5 : 14;
      const obs = this.scanObstacles(c, horizon);
      if (obs < horizon) {
        const allowed = clamp((obs - 3) * 1.1, 0, target);
        target = Math.min(target, allowed);
      }
      const nearlyStill = Math.abs(spd) < 0.6;
      if (obs < 8 && nearlyStill) c.blocked += dt;
      else c.blocked = Math.max(0, c.blocked - dt * 2);

      // honk when stuck for a while
      if (c.blocked > 2 && t >= c.nextHonk) {
        c.hornUntil = t + 0.35;
        c.nextHonk = t + 2.5 + G.rng() * 2.5;
      }
      ctl.horn = t < c.hornUntil;

      // creep around after a long block
      if (c.blocked > 6 && t >= c.aroundUntil) {
        c.aroundUntil = t + 1.6;
        c.aroundSign = this.lastObstacleSide >= 0 ? 1 : -1; // obstacle on the left => steer right
        c.blocked = 0;
      }
      if (t < c.aroundUntil) {
        steer = clamp(steer + 0.5 * c.aroundSign, -1, 1);
        target = Math.max(target, obs > 2.5 ? 3 : 0);
      }

      // ---- throttle
      let throttle: number;
      if (spd > target + 0.4) throttle = target < 0.5 ? -1 : clamp(-(spd - target) * 0.5, -1, -0.15);
      else if (spd < target - 0.3) throttle = clamp((target - spd) * 0.35 + 0.25, 0.25, 1);
      else throttle = target > 0.5 ? 0.12 : 0;
      ctl.throttle = throttle;
      ctl.steer = Math.abs(spd) < 0.3 && target < 0.5 ? 0 : steer;
      ctl.handbrake = target < 0.5 && Math.abs(spd) < 0.3;

      // ---- stale tracking (never let a jam persist forever out of sight)
      const ddx = v.pos.x - px, ddz = v.pos.z - pz;
      if (nearlyStill && ddx * ddx + ddz * ddz > 110 * 110) c.idleFar += dt;
      else c.idleFar = 0;
    }
  }

  private lastObstacleSide = 0;

  /**
   * Distance (m) from this car's front to the nearest obstacle inside the cone ahead
   * (vehicles, peds on foot, the player). Infinity when clear.
   */
  private scanObstacles(c: AiCar, horizon: number): number {
    const v = c.v;
    const s = Math.sin(v.heading), co = Math.cos(v.heading);
    const myHalf = v.spec.length / 2;
    const myW = v.spec.width;
    let best = Infinity;
    let bestSide = 0;
    const vx = v.pos.x, vz = v.pos.z;
    const reach = horizon + myHalf + 6;
    const vehicles = G.entities.vehicles;
    for (let i = 0; i < vehicles.length; i++) {
      const u = vehicles[i];
      if (u === v) continue;
      const dx = u.pos.x - vx, dz = u.pos.z - vz;
      if (dx * dx + dz * dz > reach * reach) continue;
      const f = dx * s + dz * co;
      if (f <= 0) continue;
      const side = dx * co - dz * s;
      const lat = 2.2 + Math.max(0, u.spec.width - myW) * 0.5;
      // use the other car's footprint extent along our axes
      const dh = Math.abs(Math.cos(angleDiff(v.heading, u.heading)));
      const uHalfF = (u.spec.length / 2) * dh + (u.spec.width / 2) * (1 - dh);
      const uHalfS = (u.spec.width / 2) * dh + (u.spec.length / 2) * (1 - dh);
      if (Math.abs(side) - uHalfS * 0.6 > lat) continue;
      const gap = f - uHalfF - myHalf;
      if (gap < best) {
        best = gap;
        bestSide = side;
      }
    }
    const peds = G.entities.peds;
    for (let i = 0; i < peds.length; i++) {
      const p = peds[i];
      if (p.vehicle || p.state === 'dead') continue;
      const dx = p.pos.x - vx, dz = p.pos.z - vz;
      if (dx * dx + dz * dz > reach * reach) continue;
      const f = dx * s + dz * co;
      if (f <= 0) continue;
      const side = dx * co - dz * s;
      if (Math.abs(side) > 2.2) continue;
      const gap = f - myHalf - 0.5;
      if (gap < best) {
        best = gap;
        bestSide = side;
      }
    }
    const pl = G.player;
    if (!pl.vehicle) {
      const dx = pl.pos.x - vx, dz = pl.pos.z - vz;
      if (dx * dx + dz * dz <= reach * reach) {
        const f = dx * s + dz * co;
        const side = dx * co - dz * s;
        if (f > 0 && Math.abs(side) <= 2.2) {
          const gap = f - myHalf - 0.5;
          if (gap < best) {
            best = gap;
            bestSide = side;
          }
        }
      }
    }
    this.lastObstacleSide = bestSide;
    return best;
  }
}

// ==================================================================== peds ====

export class PedSystem {
  private timer = 0;
  private burst = true;
  private unsub: (() => void)[] = [];

  constructor() {
    this.unsub.push(
      G.events.on('shot', (e) => this.scareAround(e.x, e.z, 35)),
      G.events.on('explosion', (e) => this.scareAround(e.x, e.z, 60)),
    );
  }

  private scareAround(x: number, z: number, radius: number): void {
    const r2 = radius * radius;
    const peds = G.entities.peds;
    for (let i = 0; i < peds.length; i++) {
      const p = peds[i];
      const dx = p.pos.x - x, dz = p.pos.z - z;
      if (dx * dx + dz * dz < r2) p.scare(x, z);
    }
  }

  update(dt: number): void {
    this.timer -= dt;
    if (this.timer > 0) return;
    this.timer = this.burst ? 0.05 : 0.3;
    const t = G.time.t;
    const px = G.player.pos.x, pz = G.player.pos.z;
    const peds = G.entities.peds;

    // despawn + count
    let count = 0;
    for (let i = peds.length - 1; i >= 0; i--) {
      const p = peds[i];
      if (p.persistent || p.role === 'cop' || p.vehicle) continue;
      const dx = p.pos.x - px, dz = p.pos.z - pz;
      const d2 = dx * dx + dz * dz;
      if (d2 > 160 * 160) {
        if (!p.controlled || p.state === 'dead') G.entities.removePed(p);
        continue;
      }
      if (!p.controlled && p.state !== 'dead' && p.role !== 'medic') count++;
    }

    let target = (G.isMobile ? 20 : 44) * NPC_SCALE;
    if (G.env.nightFactor > 0.5) target *= 0.6;
    const pois = G.city.pois;
    for (let i = 0; i < pois.length; i++) {
      if (pois[i].key === 'ruinbar') {
        const dx = pois[i].pos.x - px, dz = pois[i].pos.z - pz;
        if (dx * dx + dz * dz < 70 * 70) target *= 1.5;
        break;
      }
    }
    target = Math.round(target);
    if (count < target) {
      const n = this.burst ? 6 : 2;
      for (let k = 0; k < n && count < target; k++) if (this.trySpawn(px, pz)) count++;
    }
    if (this.burst && t > 1.5) this.burst = false;
  }

  private trySpawn(px: number, pz: number): boolean {
    const g = G.city.walkGraph;
    const lanes = g.lanes;
    const rng = G.rng;
    const city = G.city;
    for (let attempt = 0; attempt < 8; attempt++) {
      let x: number, z: number;
      if (lanes.length > 0) {
        const li = randomLane(g, rng);
        const lane = lanes[li];
        const k = rng();
        x = lane.a.x + (lane.b.x - lane.a.x) * k;
        z = lane.a.z + (lane.b.z - lane.a.z) * k;
      } else {
        // stub city fallback: ring around the player
        const ang = rng() * Math.PI * 2;
        const r = 25 + rng() * 80;
        x = px + Math.sin(ang) * r;
        z = pz + Math.cos(ang) * r;
      }
      const dx = x - px, dz = z - pz;
      const d = Math.hypot(dx, dz);
      if (this.burst) {
        if (d < 14 || d > 120) continue;
      } else {
        if (d < 45 || d > 120) continue;
        if (d < 90 && inView(x, z, 200)) continue;
      }
      if (city.isWater(x, z)) continue;
      if (city.resolveCircle(x, z, 0.35)) continue;
      if (G.entities.nearestPed(x, z, 1.6)) continue;
      if (G.entities.nearestVehicle(x, z, 3)) continue;
      G.entities.spawnPed('civilian', x, z);
      return true;
    }
    return false;
  }

  dispose(): void {
    for (const u of this.unsub) u();
    this.unsub.length = 0;
  }
}
